import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsInstance } from "echarts-for-react";

import { getServiceColors } from "@/features/cost/constants/weekly-cost.constants";

import theme, { CHART, ACCENT } from "@/config/theme";
import { useTheme } from "@/contexts/ThemeContext";
import type { Top5Services, WeekDetails } from "@/features/cost/types/weekly-cost-report.types";
import { ConversionToK } from "@/features/cost/utils/shared/costCalculations.utils";
import { buildTooltipForCharts } from "@/features/cost/utils/shared/costTooltip.utils";

interface CostBarChartProps {
  services?: Top5Services[] | null;
  currentWeek: WeekDetails;
  previousWeek: WeekDetails;
  onReady?: (instance: EChartsInstance) => void;
}

/**
 * CostBarChart
 *
 * @description Displays top 5 services based on current week cost
 * using a horizontal bar chart. Includes comparison with previous week
 * via tooltip.
 *
 * @param services - List of service cost data
 * @param currentWeek - Current week details (label, range, etc.)
 * @param previousWeek - Previous week details for comparison
 *
 * @returns Horizontal bar chart component
 */
const CostBarChart = ({
  services,
  currentWeek,
  previousWeek,
  onReady,
}: CostBarChartProps) => {
  const { theme: themeName } = useTheme();

  // Resolve colors fresh on each theme change; replace yellow (index 3) with green in light mode
  const serviceColors = useMemo(
    () => getServiceColors().map((c, i) =>
      i === 3 && themeName === "light" ? ACCENT.green : c
    ),
    [themeName],
  );

  const normalizedServices = useMemo(
    () => (Array.isArray(services) ? services : []),
    [services],
  );
  const hasData = normalizedServices.length > 0;

  const names = useMemo(
    () => normalizedServices.map((s) => s.serviceName),
    [normalizedServices],
  );
  const values = useMemo(
    () => normalizedServices.map((s) => s.currentCost),
    [normalizedServices],
  );

  const option = useMemo(() => ({
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: CHART.tooltipBg,
      borderColor: ACCENT.aqua,
      borderWidth: 1,
      borderRadius: 8,
      textStyle: { color: CHART.tooltipText },
      extraCssText: `box-shadow: ${CHART.tooltipShadow};`,
      confine: true,
      position: (point: number[]) => [point[0], 10],
      formatter: (params: { dataIndex: number; name: string }[]) => {
        if (!params || params.length === 0) return "";
        const p = params[0];
        if (p.dataIndex < 0 || p.dataIndex >= normalizedServices.length) return "";
        const svc = normalizedServices[p.dataIndex];
        if (!svc) return "";

        return buildTooltipForCharts({
          type: "weekly",
          title: p.name || "",
          currentLabel: currentWeek?.label || "",
          previousLabel: previousWeek?.label || "",
          currentCost: svc.currentCost || 0,
          previousCost: svc.previousCost || 0,
          chartType: "services",
        });
      },
    },

    grid: {
      top: 10,
      right: 54,
      bottom: 86,
      left: 24,
      containLabel: true,
    },

    xAxis: {
      type: "value",
      // name: "Cost ($K)",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        lineStyle: {
          type: "dashed",
          color: theme.border.subtle,
        },
      },
      axisLabel: {
        fontSize: 10,
        margin: 10,
        showMinLabel: true,
        showMaxLabel: true,
        formatter: (v: number) => `$${ConversionToK(v)}`,
        color: theme.text.tertiary,
      },
    },

    yAxis: {
      type: "category",
      data: names,
      inverse: true,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        fontSize: 11,
        color: theme.text.tertiary,
      },
    },

    series: [
      {
        type: "bar",
        data: values.map((v, i) => ({
          value: v,
          itemStyle: { color: serviceColors[i], borderRadius: [0, 4, 4, 0] },
        })),
        barMaxWidth: 20,
        label: {
          show: true,
          position: "right",
          formatter: (p: { value: number }) => `$${ConversionToK(p.value)}`,
          fontSize: 10,
          fontWeight: "600",
          color: theme.text.primary,
        },
      },
    ],
  }), [names, values, normalizedServices, currentWeek, previousWeek, themeName, serviceColors]);

  if (!hasData) {
    return (
      <div className="w-full h-full flex items-center justify-center text-text-tertiary">
        No services data available
      </div>
    );
  }

  return (
    <ReactECharts
      key={themeName}
      option={option}
      className="h-full w-full"
      notMerge
      opts={{ renderer: "canvas" }}
      onChartReady={onReady}
    />
  );
};

export default CostBarChart;
